import { useEffect, useImperativeHandle, useRef, useState } from "react";
import type { Ref } from "react";
import { Field } from "./Field";

export interface TextFieldHandle {
  /** Splice text in at the caret (replacing any selection) and keep focus. */
  insert: (text: string) => void;
}

interface TextFieldProps {
  value: string;
  /** Fired `debounceMs` after the last keystroke, on blur, and on unmount. */
  onChange: (v: string) => void;
  label?: string;
  hint?: string;
  placeholder?: string;
  maxLength?: number;
  /** Default 300 ms — one rebuild per pause, not per keystroke. */
  debounceMs?: number;
  disabled?: boolean;
  className?: string;
  /** Caret-aware insertion (the engraving symbol palette). The caret lives in
   * the DOM input this component owns, so the splice has to happen in here. */
  ref?: Ref<TextFieldHandle>;
}

/**
 * Debounced text input. Keeps the caret stable by holding the typed value
 * locally and mirroring the prop only when it changes underneath (a viewer
 * echo, a loaded design). Pending edits are flushed on blur and on unmount so
 * switching tabs mid-word never loses the text.
 */
export function TextField({
  value, onChange, label, hint, placeholder, maxLength,
  debounceMs = 300, disabled, className = "", ref,
}: TextFieldProps) {
  const [local, setLocal] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);
  /** Caret to restore once React has rendered the spliced value. */
  const caret = useRef<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pending = useRef<{ onChange: (v: string) => void; value: string | null }>({ onChange, value: null });
  pending.current.onChange = onChange;

  useEffect(() => {
    // Never clobber an edit the user is still typing / that hasn't landed yet.
    if (pending.current.value === null) setLocal(value);
  }, [value]);

  const flush = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const v = pending.current.value;
    if (v === null) return;
    pending.current.value = null;
    pending.current.onChange(v);
  };

  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => () => flushRef.current(), []);

  const edit = (v: string) => {
    setLocal(v);
    pending.current.value = v;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, debounceMs);
  };

  useEffect(() => {
    const at = caret.current;
    if (at === null) return;
    caret.current = null;
    inputRef.current?.focus();
    inputRef.current?.setSelectionRange(at, at);
  }, [local]);

  useImperativeHandle(ref, () => ({
    insert(text: string) {
      const el = inputRef.current;
      const start = el?.selectionStart ?? local.length;
      const end = el?.selectionEnd ?? local.length;
      const room = maxLength === undefined ? text.length : maxLength - local.length + (end - start);
      const piece = text.slice(0, Math.max(0, room));
      if (!piece) return;
      caret.current = start + piece.length;
      edit(local.slice(0, start) + piece + local.slice(end));
    },
  }));

  const input = (
    <input
      ref={inputRef}
      type="text"
      value={local}
      maxLength={maxLength}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => edit(e.target.value)}
      onBlur={flush}
      className={`w-full rounded-lg border border-outline-variant/60 bg-surface-container-lowest px-3 py-2.5 font-body text-[14px] text-primary outline-none focus:border-secondary disabled:opacity-50 ${className}`}
    />
  );

  return label ? <Field label={label} hint={hint}>{input}</Field> : input;
}
